import { CLASS_ACTIVE, CLASS_INITIALISED, CLASS_ALLOW_SCROLL } from "../tools/constants/classes";
import { documentDisableScroll, documentEnableScroll } from "../tools/document";
import { ANIMATION_SETTINGS } from "../settings/animation";

const SELECTOR_DRAWER =  '[data-content-drawer]';
const SELECTOR_CLOSE =   '[data-content-drawer-close]';
const SELECTOR_TRIGGER = '[data-content-drawer-trigger]';
const SELECTOR_TAB = '[data-tab]';
const SELECTOR_TAB_CONTENT = '[data-tab-content]';
const SELECTOR_BACKGROUND = '.c-product-info-drawer__background';
const SELECTOR_DYNAMIC_TABS = '[data-dynamic-tabs]';
const SELECTOR_DYNAMIC_TAB = '[data-dynamic-tab]';
const SELECTOR_DYNAMIC_TAB_CONTENT = '[data-dynamic-tab-content]';

const CLASS_TOP_DRAWER = 'is-top';
const CLASS_BACK_DRAWER = 'is-last';
const CLASS_SECONDARY_DRAWER = 'is-secondary';
const CLASS_TERTIARY_DRAWER = 'is-tertiary';
const CLASS_DRAWER_ACTIVE = 'product-info-drawer-active';

let drawers: NodeListOf<HTMLElement>;
let closes: NodeListOf<HTMLButtonElement>;
let triggers: NodeListOf<HTMLButtonElement>;
let tabs: NodeListOf<HTMLButtonElement>;
let dynamicTabs: NodeListOf<HTMLButtonElement>;

rebindContentDrawerEvents();

export function rebindContentDrawerEvents() {
  drawers = document.querySelectorAll(SELECTOR_DRAWER);
  closes = document.querySelectorAll(SELECTOR_CLOSE);
  triggers = document.querySelectorAll(SELECTOR_TRIGGER);
  tabs = document.querySelectorAll(SELECTOR_TAB);
  dynamicTabs = document.querySelectorAll(SELECTOR_DYNAMIC_TAB);

  triggers.forEach(trigger => {
    if (trigger.classList.contains(CLASS_INITIALISED)) {
      const newTrigger = trigger.cloneNode(true) as HTMLButtonElement;
      trigger.parentNode?.replaceChild(newTrigger, trigger);
      newTrigger.addEventListener('click', (e) => onTriggerClick(e));
      newTrigger.classList.add(CLASS_INITIALISED);
    } else {
      trigger.addEventListener('click', (e) => onTriggerClick(e));
      trigger.classList.add(CLASS_INITIALISED);
    }
  });

  closes.forEach(close => {
    if (!close.classList.contains(CLASS_INITIALISED)) {
      close.addEventListener('click', (e) => onCloseClick(e));
      close.classList.add(CLASS_INITIALISED);
    }
  });

  const backgrounds = document.querySelectorAll(SELECTOR_BACKGROUND);
  backgrounds.forEach(background => {
    if (!background.classList.contains(CLASS_INITIALISED)) {
      background.addEventListener('click', (e) => onBackgroundClick(e));
      background.classList.add(CLASS_INITIALISED);
    }
  });

  tabs.forEach(tab => {
    if (!tab.classList.contains(CLASS_INITIALISED)) {
      tab.addEventListener('click', (e) => onTabClick(e));
      tab.classList.add(CLASS_INITIALISED);
    }
  });

  dynamicTabs.forEach(tab => {
    if (!tab.classList.contains(CLASS_INITIALISED)) {
      tab.addEventListener('click', (e) => onDynamicTabClick(e));
      tab.classList.add(CLASS_INITIALISED);
    }
  });
}

export function rebindCloseButtons() {
  const newCloses = document.querySelectorAll(SELECTOR_CLOSE);
  newCloses.forEach(close => {
    const newClose = close.cloneNode(true) as HTMLButtonElement;
    close.parentNode?.replaceChild(newClose, close);
    
    newClose.addEventListener('click', (e) => onCloseClick(e));
    newClose.classList.add(CLASS_INITIALISED);
  });
}

function onTriggerClick(e: Event) {
  const trigger = e.currentTarget as HTMLButtonElement;
  const drawerId = trigger.dataset.contentDrawerTrigger;
  
  const allDrawers = document.querySelectorAll<HTMLElement>(SELECTOR_DRAWER);
  const drawerToOpen = Array.from(allDrawers).find(
    drawer => drawer.dataset.contentDrawer === drawerId
  );
  
  if (drawerToOpen) {
    const targetTab = trigger.dataset.tabTarget;
    openContentDrawer(drawerToOpen, targetTab);
  }
}

function onCloseClick(e: Event) {
  e.preventDefault();
  e.stopPropagation();
  const close = e.currentTarget as HTMLButtonElement;
  
  let thisDrawer = close.closest(SELECTOR_DRAWER) as HTMLElement;
  
  if (!thisDrawer) {
    const background = close.closest('.c-product-info-drawer__background') as HTMLElement;
    if (background) {
      let nextSibling = background.nextElementSibling;
      while (nextSibling) {
        if (nextSibling.matches && nextSibling.matches(SELECTOR_DRAWER)) {
          thisDrawer = nextSibling as HTMLElement;
          break;
        }
        nextSibling = nextSibling.nextElementSibling;
      }
    }
  }
  
  if (!thisDrawer) {
    thisDrawer = document.querySelector(`${SELECTOR_DRAWER}.${CLASS_ACTIVE}`) as HTMLElement;
  }
  
  if (!thisDrawer) {
    return;
  }
  closeContentDrawer(thisDrawer);
}

function onBackgroundClick(e: Event) {
  const background = e.currentTarget as HTMLElement;
  
  let thisDrawer = background.nextElementSibling as HTMLElement;
  while (thisDrawer) {
    if (thisDrawer.matches && thisDrawer.matches(SELECTOR_DRAWER)) {
      break;
    }
    thisDrawer = thisDrawer.nextElementSibling as HTMLElement;
  }
  
  if (!thisDrawer) {
    thisDrawer = document.querySelector(`${SELECTOR_DRAWER}.${CLASS_ACTIVE}`) as HTMLElement;
  }
  
  if (!thisDrawer) return;
  closeContentDrawer(thisDrawer);
}

function onTabClick(e: Event) {
  const tab = e.currentTarget as HTMLButtonElement;
  const tabId = tab.dataset.tab;
  const drawer = tab.closest(SELECTOR_DRAWER) as HTMLElement;
  
  if (tabId && drawer) {
    switchTab(drawer, tabId);
  }
}

function onDynamicTabClick(e: Event) {
  const tab = e.currentTarget as HTMLButtonElement;
  const tabId = tab.dataset.dynamicTab;
  const dynamicTabsContainer = tab.closest(SELECTOR_DYNAMIC_TABS) as HTMLElement;
  
  if (tabId && dynamicTabsContainer) {
    switchDynamicTab(dynamicTabsContainer, tabId);
  }
}

export function openContentDrawer(thisDrawer: HTMLElement, targetTab?: string) {
  if (!thisDrawer) return;

  const allDrawers = document.querySelectorAll<HTMLElement>(SELECTOR_DRAWER);
  let currentDrawers = Array.from(allDrawers).filter(drawer => drawer.classList.contains(CLASS_ACTIVE));
  
  currentDrawers.forEach(drawer => drawer.classList.remove(CLASS_TOP_DRAWER));

  if (currentDrawers.length === 1) {
    currentDrawers[0].classList.add(CLASS_SECONDARY_DRAWER);
  } else if (currentDrawers.length === 2) {
    currentDrawers[0].classList.add(CLASS_TERTIARY_DRAWER);
  }

  thisDrawer.classList.add(CLASS_ACTIVE, CLASS_TOP_DRAWER);
  
  // Add class if it's a shipping-returns drawer
  if (thisDrawer.dataset.contentDrawer === 'shipping-returns') {
    thisDrawer.classList.add('is-shipping-drawer');
    document.body.classList.add('is-shipping-drawer-open');
  }
  
  if (thisDrawer.classList.contains('c-product-info-drawer')) {
    document.body.classList.add(CLASS_DRAWER_ACTIVE);
    
    // override lenis scroll
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    
    document.body.setAttribute('data-scroll-y', scrollY.toString());
    
    const scrollableElements = thisDrawer.querySelectorAll('.c-product-info-drawer__mobile-content, .c-product-info-drawer__content, .c-product-info-drawer__tabs-desktop, .c-product-info-drawer__tabs-scroll');
    scrollableElements.forEach(element => {
      // element.setAttribute('data-lenis-prevent', '');
      allowScrollingOnDrawerContent(element as HTMLElement);

    });
    
    thisDrawer.setAttribute('data-lenis-prevent', '');
  }
  
  let background: HTMLElement | null = thisDrawer.previousElementSibling as HTMLElement;
  if (!background || !background.classList.contains('c-product-info-drawer__background')) {
    const allBackgrounds = document.querySelectorAll<HTMLElement>(SELECTOR_BACKGROUND);
    background = Array.from(allBackgrounds).find(bg => {
      const nextSibling = bg.nextElementSibling;
      return nextSibling === thisDrawer;
    }) || null;
  }
  
  if (background && background.classList.contains('c-product-info-drawer__background')) {
    background.classList.add(CLASS_ACTIVE);
    if (thisDrawer.dataset.contentDrawer === 'shipping-returns') {
      background.classList.add('is-shipping-drawer-background');
    }
  }

  if (targetTab) {
    switchTab(thisDrawer, targetTab);
  } else {
    const activeTabContent = thisDrawer.querySelector('.c-product-info-drawer__tab-content.is-active');
    if (activeTabContent) {
      wrapTablesInContent(activeTabContent as HTMLElement);
    }
  }
}

export function closeContentDrawer(thisDrawer: HTMLElement) {
  thisDrawer.classList.remove(CLASS_ACTIVE, 'is-shipping-drawer');
  
  if (thisDrawer.dataset.contentDrawer === 'shipping-returns') {
    document.body.classList.remove('is-shipping-drawer-open');
  }

  if (thisDrawer.classList.contains('c-product-info-drawer')) {
    document.body.classList.remove(CLASS_DRAWER_ACTIVE);
    
    const scrollY = document.body.getAttribute('data-scroll-y');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    document.body.removeAttribute('data-scroll-y');
    
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY));
    }
    
    // Remove Lenis prevent attributes to restore smooth scrolling
    const scrollableElements = thisDrawer.querySelectorAll('.c-product-info-drawer__mobile-content, .c-product-info-drawer__content, .c-product-info-drawer__tabs-desktop, .c-product-info-drawer__tabs-scroll');
    scrollableElements.forEach(element => {
      element.removeAttribute('data-lenis-prevent');
      element.removeAttribute('data-scroll-initialized');
      element.classList.remove(CLASS_ALLOW_SCROLL);
    });
    
    thisDrawer.removeAttribute('data-lenis-prevent');
  }
  
  // Remove shipping drawer class from background
  const background = thisDrawer.previousElementSibling as HTMLElement;
  if (background && background.classList.contains('c-product-info-drawer__background')) {
    background.classList.remove('is-shipping-drawer-background');
  }

  let currentDrawers = Array.from(drawers).filter(drawer => drawer.classList.contains(CLASS_ACTIVE));

  if (currentDrawers.length === 2) {
    currentDrawers[0].classList.remove(CLASS_TERTIARY_DRAWER);
  } else if (currentDrawers.length === 1) {
    currentDrawers[0].classList.remove(CLASS_SECONDARY_DRAWER);
  }

  if (!currentDrawers.length) {
    const background = thisDrawer.previousElementSibling as HTMLElement;
    if (background && background.classList.contains('c-product-info-drawer__background')) {
      background.classList.remove(CLASS_ACTIVE);
    }
  }

  setTimeout(() => {
    thisDrawer.classList.remove(CLASS_TOP_DRAWER, CLASS_SECONDARY_DRAWER, CLASS_TERTIARY_DRAWER);
  }, ANIMATION_SETTINGS.durationMedium);

  if (!currentDrawers.length) {
    documentEnableScroll();
  }

  document.body.style.removeProperty('overflow');
}

export function closeAllContentDrawers() {
  if (Array.from(drawers).some(drawer => drawer.classList.contains('c-product-info-drawer') && drawer.classList.contains(CLASS_ACTIVE))) {
    document.body.classList.remove(CLASS_DRAWER_ACTIVE);
  }

  drawers.forEach(drawer => {
    if (drawer.classList.contains(CLASS_ACTIVE)) {
      const background = drawer.previousElementSibling as HTMLElement;
      if (background && background.classList.contains('c-product-info-drawer__background')) {
        background.classList.remove(CLASS_ACTIVE);
      }
      
      if (drawer.classList.contains('c-product-info-drawer')) {
        const scrollableElements = drawer.querySelectorAll('.c-product-info-drawer__mobile-content, .c-product-info-drawer__content, .c-product-info-drawer__tabs-desktop, .c-product-info-drawer__tabs-scroll');
        scrollableElements.forEach(element => {
          element.removeAttribute('data-lenis-prevent');
          element.removeAttribute('data-scroll-initialized');
          element.classList.remove(CLASS_ALLOW_SCROLL);
        });
        
        drawer.removeAttribute('data-lenis-prevent');
      }
      
      closeContentDrawer(drawer);
    }
  });

  setTimeout(() => {
    if (!Array.from(drawers).some(drawer => drawer.classList.contains(CLASS_ACTIVE))) {
      documentEnableScroll();
    }
  }, ANIMATION_SETTINGS.durationMedium);
}

function wrapTablesInContent(contentElement: HTMLElement) {
  const contentBody = contentElement.querySelector('.c-product-info-drawer__content-body');
  
  if (!contentBody || !contentBody.classList.contains('has-table')) return;
  
  const tables = contentBody.querySelectorAll('table:not(.c-product-info-drawer__table-wrapper table)');
  
  tables.forEach((table) => {
    if (table.parentElement?.classList.contains('c-product-info-drawer__table-wrapper')) {
      return;
    }
    
    const wrapper = document.createElement('div');
    wrapper.className = 'c-product-info-drawer__table-wrapper';
    
    table.parentNode?.insertBefore(wrapper, table);
    
    wrapper.appendChild(table);
  });
}

function wrapTablesInDynamicContent(contentElement: HTMLElement) {
  const tables = contentElement.querySelectorAll('table:not(.c-product-info-drawer__table-wrapper table)');
  
  tables.forEach((table) => {
    if (table.parentElement?.classList.contains('c-product-info-drawer__table-wrapper')) {
      return;
    }
    
    const wrapper = document.createElement('div');
    wrapper.className = 'c-product-info-drawer__table-wrapper';
    
    table.parentNode?.insertBefore(wrapper, table);
    
    wrapper.appendChild(table);
  });
}

function switchTab(drawer: HTMLElement, tabId: string) {
  const allTabs = drawer.querySelectorAll(SELECTOR_TAB);
  const allTabContents = drawer.querySelectorAll(SELECTOR_TAB_CONTENT);
  
  allTabs.forEach(tab => tab.classList.remove(CLASS_ACTIVE));
  allTabContents.forEach(content => content.classList.remove(CLASS_ACTIVE));
  
  const targetTabs = drawer.querySelectorAll(`[data-tab="${tabId}"]`);
  const targetContents = drawer.querySelectorAll(`[data-tab-content="${tabId}"]`);
  
  targetTabs.forEach(tab => tab.classList.add(CLASS_ACTIVE));
  targetContents.forEach(content => {
    content.classList.add(CLASS_ACTIVE);
    wrapTablesInContent(content as HTMLElement);
  });
  
  // Scroll to active tab if it's in a scrollable container
  targetTabs.forEach(tab => {
    scrollToActiveTab(tab as HTMLElement);
  });
}

function switchDynamicTab(dynamicTabsContainer: HTMLElement, tabId: string) {
  const allDynamicTabs = dynamicTabsContainer.querySelectorAll(SELECTOR_DYNAMIC_TAB);
  const allDynamicTabContents = dynamicTabsContainer.querySelectorAll(SELECTOR_DYNAMIC_TAB_CONTENT);
  
  allDynamicTabs.forEach(tab => tab.classList.remove(CLASS_ACTIVE));
  allDynamicTabContents.forEach(content => content.classList.remove(CLASS_ACTIVE));
  
  const targetDynamicTabs = dynamicTabsContainer.querySelectorAll(`[data-dynamic-tab="${tabId}"]`);
  const targetDynamicContents = dynamicTabsContainer.querySelectorAll(`[data-dynamic-tab-content="${tabId}"]`);
  
  targetDynamicTabs.forEach(tab => tab.classList.add(CLASS_ACTIVE));
  targetDynamicContents.forEach(content => {
    content.classList.add(CLASS_ACTIVE);
    wrapTablesInDynamicContent(content as HTMLElement);
  });
  
  // Scroll to active dynamic tab if it's in a scrollable container
  targetDynamicTabs.forEach(tab => {
    scrollToActiveTab(tab as HTMLElement);
  });
}

/**
 * Allow scrolling on drawer content by bypassing Lenis scroll prevention
 */
export function allowScrollingOnDrawerContent(drawerContent: HTMLElement) {
  // Remove lenis prevent attributes
  drawerContent.removeAttribute('data-lenis-prevent');
  drawerContent.removeAttribute('data-lenis-prevent-wheel');
  drawerContent.removeAttribute('data-lenis-prevent-touch');

  // Add class to allow scrolling (CSS fallback)
  drawerContent.classList.add(CLASS_ALLOW_SCROLL);

  // Ensure element has proper overflow styles for scrolling
  const computedStyle = window.getComputedStyle(drawerContent);
  const isTabsScroll = drawerContent.classList.contains('c-product-info-drawer__tabs-scroll');
  const isMobileContent = drawerContent.classList.contains('c-product-info-drawer__mobile-content');
  const isContent = drawerContent.classList.contains('c-product-info-drawer__content');
  
  if (isTabsScroll) {
    if (computedStyle.overflowX === 'visible' || computedStyle.overflowX === 'hidden') {
      drawerContent.style.overflowX = 'auto';
    }
  } else if (isMobileContent || isContent) {
    drawerContent.style.overflowY = 'auto';
    if (isMobileContent) {
      const maxHeight = computedStyle.maxHeight;
      if (!maxHeight || maxHeight === 'none') {
        drawerContent.style.maxHeight = 'calc(100dvh - 8.125rem)';
      }
    } else if (isContent) {
      const parent = drawerContent.parentElement;
      if (parent) {
        const parentStyle = window.getComputedStyle(parent);
        if (parentStyle.display === 'flex' && parentStyle.flexDirection === 'row') {
          drawerContent.style.height = '100%';
        }
      }
    }
  }

  const canScroll = isTabsScroll
    ? drawerContent.scrollWidth > drawerContent.clientWidth
    : drawerContent.scrollHeight > drawerContent.clientHeight;
  
  const shouldAlwaysEnable = isMobileContent || isContent;
  
  if ((canScroll || shouldAlwaysEnable) && !drawerContent.hasAttribute('data-scroll-initialized')) {
    drawerContent.setAttribute('data-scroll-initialized', 'true');
    
    drawerContent.addEventListener('wheel', handleManualScroll as EventListener, { passive: false, capture: true });
    drawerContent.addEventListener('touchstart', handleTouchStart as EventListener, { passive: false, capture: true });
    drawerContent.addEventListener('touchmove', handleTouchMove as EventListener, { passive: false, capture: true });
    
    if (isTabsScroll) {
      drawerContent.addEventListener('touchstart', handleHorizontalTouchStart as EventListener, { passive: false, capture: true });
      drawerContent.addEventListener('touchmove', handleHorizontalTouchMove as EventListener, { passive: false, capture: true });
    }
  }
}

/**
 * Handle manual wheel scrolling to bypass Lenis
 */
function handleManualScroll(e: Event): void {
  const wheelEvent = e as WheelEvent;
  const target = wheelEvent.currentTarget as HTMLElement;
  if (!target) return;
  
  const isTabsScroll = target.classList.contains('c-product-info-drawer__tabs-scroll');
  const canScroll = isTabsScroll
    ? target.scrollWidth > target.clientWidth
    : target.scrollHeight > target.clientHeight;
  
  if (!canScroll) {
    return;
  }
  
  const isAtTop = target.scrollTop <= 1;
  const isAtBottom = target.scrollTop >= (target.scrollHeight - target.clientHeight - 1);
  const isAtLeft = target.scrollLeft <= 1;
  const isAtRight = target.scrollLeft >= (target.scrollWidth - target.clientWidth - 1);
  
  if (isTabsScroll) {
    if ((wheelEvent.deltaY < 0 && isAtLeft) || (wheelEvent.deltaY > 0 && isAtRight)) {
      return;
    }
  } else {
    if ((wheelEvent.deltaY < 0 && isAtTop) || (wheelEvent.deltaY > 0 && isAtBottom)) {
      return;
    }
  }
  
  wheelEvent.preventDefault();
  wheelEvent.stopPropagation();
  wheelEvent.stopImmediatePropagation();
  
  if (isTabsScroll) {
    target.scrollLeft += wheelEvent.deltaY;
  } else {
    target.scrollTop += wheelEvent.deltaY;
  }
}

/**
 * Handle touch start for manual touch scrolling
 */
let touchStartY = 0;
function handleTouchStart(e: Event): void {
  const touchEvent = e as TouchEvent;
  touchStartY = touchEvent.touches[0].clientY;
}

/**
 * Handle touch move for manual touch scrolling
 */
function handleTouchMove(e: Event): void {
  const touchEvent = e as TouchEvent;
  const target = touchEvent.currentTarget as HTMLElement;
  if (!target) return;
  
  const canScroll = target.scrollHeight > target.clientHeight;
  if (!canScroll) {
    return;
  }

  touchEvent.preventDefault();
  touchEvent.stopPropagation();
  touchEvent.stopImmediatePropagation();

  const touchY = touchEvent.touches[0].clientY;
  const deltaY = touchStartY - touchY;

  target.scrollTop += deltaY;
  touchStartY = touchY;
}

/**
 * Handle horizontal touch start for horizontal scrolling
 */
let touchStartX = 0;
function handleHorizontalTouchStart(e: Event): void {
  const touchEvent = e as TouchEvent;
  touchStartX = touchEvent.touches[0].clientX;
}

/**
 * Handle horizontal touch move for horizontal scrolling
 */
function handleHorizontalTouchMove(e: Event): void {
  const touchEvent = e as TouchEvent;
  const target = touchEvent.currentTarget as HTMLElement;
  if (!target) return;
  
  const canScroll = target.scrollWidth > target.clientWidth;
  if (!canScroll) {
    return;
  }

  touchEvent.preventDefault();
  touchEvent.stopPropagation();
  touchEvent.stopImmediatePropagation();

  const touchX = touchEvent.touches[0].clientX;
  const deltaX = touchStartX - touchX;

  target.scrollLeft += deltaX;
  touchStartX = touchX;
}

/**
 * Scroll to active tab in scrollable container
 */
function scrollToActiveTab(activeTab: HTMLElement): void {
  const scrollContainer = activeTab.closest('.c-product-info-drawer__tabs-scroll');
  
  if (scrollContainer) {
    const container = scrollContainer as HTMLElement;
    const tabRect = activeTab.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const tabLeft = tabRect.left - containerRect.left + container.scrollLeft;
    const tabRight = tabLeft + tabRect.width;
    const containerWidth = container.clientWidth;
    const containerScrollLeft = container.scrollLeft;
    
    const isTabVisible = tabLeft >= containerScrollLeft && tabRight <= containerScrollLeft + containerWidth;
    
    if (!isTabVisible) {
      const targetScrollLeft = tabLeft - (containerWidth / 2) + (tabRect.width / 2);
      
      // Smooth scroll to the tab
      container.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: 'smooth'
      });
    }
  }
}
